import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { Table } from '@/components/ui/Table';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/form/Input';
import { useToast } from '@/components/ui/useToast';
import { useConfirm } from '@/components/ui/useConfirm';
import { apiError } from '@/lib/apiError';
import { formatINR } from '@/lib/format';
import { listCostCentres, createCostCentre, alterCostCentre, deleteCostCentre, type CostCentre } from '@/api/accountsBooks';

const label = 'mb-1 block text-xs font-semibold text-[var(--text-secondary)]';

export function CostCentresPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirmDlg = useConfirm();
  const [draft, setDraft] = useState<{ id: number | null; name: string } | null>(null);

  const query = useQuery({ queryKey: ['acc-books', 'cost-centres'], queryFn: listCostCentres });
  const rows = query.data ?? [];
  const refresh = () => qc.invalidateQueries({ queryKey: ['acc-books', 'cost-centres'] });

  const saveMut = useMutation({
    mutationFn: (d: { id: number | null; name: string }) => (d.id ? alterCostCentre(d.id, d.name.trim()) : createCostCentre(d.name.trim())),
    onSuccess: () => { toast.push('Cost centre saved', 'success'); setDraft(null); refresh(); },
    onError: (e) => toast.push(apiError(e, 'Save failed'), 'error'),
  });
  const delMut = useMutation({
    mutationFn: (id: number) => deleteCostCentre(id),
    onSuccess: () => { toast.push('Cost centre deleted', 'success'); refresh(); },
    onError: (e) => toast.push(apiError(e, 'Delete failed'), 'error'),
  });

  return (
    <div>
      <SectionHeader
        title="Cost Centres"
        actions={<Button variant="primary" size="sm" onClick={() => setDraft({ id: null, name: '' })}><Plus size={14} /> New cost centre</Button>}
      />

      <Table<CostCentre>
        storageKey="acc-books-cost-centres"
        title="Cost Centres"
        count={`${rows.length} cost centre${rows.length === 1 ? '' : 's'}`}
        rows={rows}
        rowKey={(r) => r.id}
        loading={query.isFetching && rows.length === 0}
        empty="No cost centres."
        columns={[
          { key: 'name', header: 'Department', minWidth: '160px', cell: (r) => <strong>{r.name}</strong> },
          { key: 'entries', header: 'Entries', width: '90px', align: 'right', cell: (r) => r.entries },
          { key: 'amount', header: 'Net amount', width: '140px', align: 'right', cell: (r) => (r.amount ? `₹${formatINR(Math.abs(r.amount))} ${r.amount > 0 ? 'Dr' : 'Cr'}` : '—') },
          {
            key: 'actions', header: '', width: '110px', align: 'right', alwaysVisible: true,
            cell: (r) => (
              <div className="flex items-center justify-end gap-1">
                <Button variant="glass" size="sm" onClick={() => setDraft({ id: r.id, name: r.name })}>Rename</Button>
                {r.entries === 0 && (
                  <button type="button" onClick={async () => { if (await confirmDlg(`Delete cost centre ${r.name}?`)) delMut.mutate(r.id); }} className="rounded p-1.5 text-danger hover:bg-danger/10" title="Delete cost centre"><Trash2 size={14} /></button>
                )}
              </div>
            ),
          },
        ]}
      />

      <Modal
        open={!!draft}
        onClose={() => setDraft(null)}
        title={draft?.id ? 'Rename Cost Centre' : 'New Cost Centre'}
        size="sm"
        footer={
          <>
            <Button variant="glass" onClick={() => setDraft(null)}>Cancel</Button>
            <Button variant="primary" disabled={saveMut.isPending} onClick={() => draft && saveMut.mutate(draft)}>
              {saveMut.isPending ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      >
        {draft && (
          <div>
            <label className={label}>Name</label>
            <Input value={draft.name} maxLength={100} autoFocus placeholder="e.g. Marketing" onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </div>
        )}
      </Modal>
    </div>
  );
}
